import type { Vec2 } from './vec';
import type { Triangle } from './triangle';
import { add, sub, scale, dist } from './vec';
import { angleAt } from './angle';

/**
 * Dilate a point relative to a center with scale factor k.
 * Formula: result = center + k·(p − center)
 */
export function dilatePoint(p: Vec2, center: Vec2, k: number): Vec2 {
  const offset = sub(p, center);
  const scaled = scale(offset, k);
  return add(center, scaled);
}

/**
 * Compute the intercept ratios for a line parallel to BC cutting sides AB and AC.
 * Given a triangle ABC and a fraction t ∈ (0,1):
 * - D = A + t·(B−A) on side AB
 * - E = A + t·(C−A) on side AC
 * Both D and E divide their respective sides in the same ratio: t/(1−t)
 *
 * @param tri Triangle {a, b, c}
 * @param t Fraction along AB and AC (must be in (0,1) for non-degenerate cut)
 * @returns [AD/DB, AE/EC] = [t/(1−t), t/(1−t)]
 */
export function interceptRatios(_tri: Triangle, t: number): [number, number] {
  // For a cut at fraction t along AB and AC from apex A:
  // AD = t·|AB|, DB = (1−t)·|AB|, so AD/DB = t/(1−t)
  // AE = t·|AC|, EC = (1−t)·|AC|, so AE/EC = t/(1−t)
  const ratio = t / (1 - t);
  return [ratio, ratio];
}

/**
 * Determine if triangle B is similar to triangle A.
 * Returns the vertex correspondence mapping and scale factor k, or null if not similar.
 *
 * @param triA The base triangle
 * @param triB The triangle to compare
 * @param tol Angle tolerance in degrees for matching
 * @returns { mapping, k } where mapping[i] is the vertex in triB corresponding to triA's vertex i,
 *          and k = |B corresponding side| / |A corresponding side|, or null if not similar
 */
export function similarCorrespondence(
  triA: Triangle,
  triB: Triangle,
  tol: number,
): { mapping: [number, number, number]; k: number } | null {
  // Compute interior angles of both triangles
  const anglesA = [
    angleAt(triA.a, triA.b, triA.c), // angle at A
    angleAt(triA.b, triA.a, triA.c), // angle at B
    angleAt(triA.c, triA.a, triA.b), // angle at C
  ];

  const anglesB = [
    angleAt(triB.a, triB.b, triB.c), // angle at A
    angleAt(triB.b, triB.a, triB.c), // angle at B
    angleAt(triB.c, triB.a, triB.b), // angle at C
  ];

  // Vertices and sides of both triangles
  const verticesA = [triA.a, triA.b, triA.c];
  const verticesB = [triB.a, triB.b, triB.c];

  // All 6 possible correspondences: 3 rotations and 3 reflections
  // Rotations: [0,1,2], [1,2,0], [2,0,1]
  // Reflections: [0,2,1], [2,1,0], [1,0,2]
  const possibleMappings: Array<[number, number, number]> = [
    [0, 1, 2], // direct
    [1, 2, 0], // rotate +1
    [2, 0, 1], // rotate +2
    [0, 2, 1], // reflect
    [2, 1, 0], // reflect+rotate
    [1, 0, 2], // reflect+rotate
  ];

  for (const mapping of possibleMappings) {
    // Check if all three angles match within tolerance
    let allMatch = true;
    for (let i = 0; i < 3; i++) {
      const angleA = anglesA[i]!;
      const angleBCorresponding = anglesB[mapping[i]!]!;
      if (Math.abs(angleA - angleBCorresponding) > tol) {
        allMatch = false;
        break;
      }
    }

    if (allMatch) {
      // Found a matching correspondence. Compute the scale factor k.
      // Use the first pair of corresponding sides to compute k.
      const sideA_0_1 = dist(verticesA[0]!, verticesA[1]!);
      const sideB_map0_map1 = dist(verticesB[mapping[0]]!, verticesB[mapping[1]]!);
      const k = sideB_map0_map1 / sideA_0_1;

      return {
        mapping,
        k,
      };
    }
  }

  return null;
}
